import React from "react";
import { Card, CardContent, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { colors } from "common";

import DateRangeIcon from "@mui/icons-material/DateRange";
import SettingsIcon from "@mui/icons-material/Settings";
import BarChartIcon from "@mui/icons-material/BarChart";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import DescriptionIcon from "@mui/icons-material/Description";
import { Theme } from "@mui/material/styles";
import BuildsGalleryTags from "components/builds-gallery/components/BuildGalleryTags";
import { BuildsGalleryPrice } from "components/gallery/GalleryBuildPrice";
import BazarButton from "components/BazarButton";
import ShoppingCartIcon from "@mui/icons-material/AddShoppingCart";

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(5),
    },
    header: {
      backgroundColor: colors.primary,
      padding: theme.spacing(1),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    title: {
      fontSize: 25,
      fontWeight: 900,
      letterSpacing: 0.5,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(3),
    },
    cardContentBody: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    iconAndTitle: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 150,
      [theme.breakpoints.down("md")]: {
        minWidth: 120,
      },
    },
    detailsRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      wordWrap: "break-word",
    },
    contentTitle: {
      marginLeft: theme.spacing(0.5),
      color: colors.grey[700],
      [theme.breakpoints.down("md")]: {
        fontSize: 15,
      },
    },
    content: {
      color: colors.primary,
      marginLeft: theme.spacing(2),
      display: "flex",
      flex: 1,
      [theme.breakpoints.down("md")]: {
        marginLeft: theme.spacing(0),
      },
    },
    tagsSection: {
      display: "flex",
      flexDirection: "row",
      color: colors.primary,
      marginLeft: theme.spacing(2),
      flexWrap: "wrap",
      overflow: "hidden",
      [theme.breakpoints.down("md")]: {
        marginLeft: theme.spacing(0),
      },
    },
    disclaimerRoot: {
      marginTop: theme.spacing(2),
    },
    disclaimerContainer: {
      fontWeight: 500,
      letterSpacing: 0.75,
      fontSize: 10,
      color: colors.grey[400],
    },
  }),
  { index: 999 }
);

const component = ({ buildDetails, isLoading = false, onClickAddToCart }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const classes = useStyles();

  return (
    <Card className={classes.root} elevation={3}>
      <div className={classes.header}>
        <Typography color={colors.white} fontSize={16} fontWeight={700}>
          BUILD DETAILS
        </Typography>
      </div>
      {buildDetails ? (
        <CardContent className={classes.cardContentBody}>
          <Typography className={classes.title} gutterBottom>
            {(buildDetails && buildDetails?.title
              ? buildDetails?.title
              : "PC WORTH BUILD"
            ).toUpperCase()}
            {isLoading ? null : <BuildsGalleryPrice data={buildDetails} />}
            {isLoading ? null : ""}
          </Typography>

          <BazarButton
            color="primary"
            variant="contained"
            onClick={onClickAddToCart}
            sx={{ mb: 4.5, px: "1.75rem", height: 40 }}
          >
            <ShoppingCartIcon />
            Add to Cart
          </BazarButton>

          {buildDetails.created_at ? (
            <div className={classes.detailsRow}>
              <div className={classes.iconAndTitle}>
                <DateRangeIcon fontSize="small" color="action" />
                <Typography className={classes.contentTitle} fontSize={16}>
                  Build Date:
                </Typography>
              </div>
              <Typography className={classes.content} fontSize={16}>
                {buildDetails.created_at}
              </Typography>
            </div>
          ) : null}

          {buildDetails?.cpu ? (
            <div className={classes.detailsRow}>
              <div className={classes.iconAndTitle}>
                <SettingsIcon fontSize="small" color="action" />
                <Typography className={classes.contentTitle} fontSize={16}>
                  CPU:
                </Typography>
              </div>
              <Typography className={classes.content} fontSize={16}>
                {buildDetails?.cpu || ""}
              </Typography>
            </div>
          ) : null}

          {buildDetails?.build_tier ? (
            <div className={classes.detailsRow}>
              <div className={classes.iconAndTitle}>
                <BarChartIcon fontSize="small" color="action" />
                <Typography className={classes.contentTitle} fontSize={16}>
                  Build Tier:
                </Typography>
              </div>
              <Typography className={classes.content} fontSize={16}>
                {buildDetails?.build_tier || ""}
              </Typography>
            </div>
          ) : null}

          {buildDetails?.build_tags && buildDetails?.build_tags.length > 0 ? (
            <div className={classes.detailsRow}>
              <div className={classes.iconAndTitle}>
                <LabelImportantIcon fontSize="small" color="action" />
                <Typography className={classes.contentTitle} fontSize={16}>
                  Tags:
                </Typography>
              </div>
              <div className={classes.tagsSection}>
                {(buildDetails?.build_tags || []).map((tag, i) => (
                  <BuildsGalleryTags key={`${tag}${i}`} title={tag} />
                ))}
              </div>
            </div>
          ) : null}

          {buildDetails?.quote ? (
            <div className={classes.detailsRow}>
              <div className={classes.iconAndTitle}>
                <FormatQuoteIcon fontSize="small" color="action" />
                <Typography className={classes.contentTitle} fontSize={16}>
                  Quote:
                </Typography>
              </div>
              <Typography className={classes.content} fontSize={16}>
                {`"${buildDetails?.quote}"`}
              </Typography>
            </div>
          ) : null}

          {buildDetails?.description ? (
            <div className={classes.detailsRow}>
              <div className={classes.iconAndTitle}>
                <DescriptionIcon fontSize="small" color="action" />
                <Typography className={classes.contentTitle} fontSize={16}>
                  Description:
                </Typography>
              </div>
              <Typography className={classes.content} fontSize={16}>
                {buildDetails?.description}
              </Typography>
            </div>
          ) : null}

          <div className={classes.disclaimerRoot}>
            <Typography className={classes.disclaimerContainer}>
              PRICE AND STOCK(S) ARE SUBJECT TO CHANGE WITHOUT ANY PRIOR NOTICE
              AND SHIPPING FEES NOT YET INCLUDED
            </Typography>
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
};

export const BuildGalleryDetails = React.memo(component);
